import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import {
  XMarkIcon,
  FireIcon,
  TruckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  ClockIcon,
  TrashIcon,
  CogIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { fuelTransactionsService } from '../../services/logistics/fuel-transactions';
import { shipmentsService } from '../../services/logistics/shipments';
import { shipmentCostingsService } from '../../services/logistics/shipment-costings';
import { enhancedFuelImportService } from '../../services/logistics/fuel-import-enhanced';
import { bulkFuelOperationsService } from '../../services/logistics/bulk-fuel-operations';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatNumber } from '../../utils/formatting';
import VehicleMappingManagementModal from './VehicleMappingManagementModal';
import toast from 'react-hot-toast';
import './fuel-import-modal-fixes.css';

const BulkFuelCostManager = ({ isOpen, onClose, companyId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [fuelTransactions, setFuelTransactions] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [costings, setCostings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, applied, unapplied, misaligned
  const [processing, setProcessing] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [showReAlignment, setShowReAlignment] = useState(false);
  const [reAlignmentResults, setReAlignmentResults] = useState(null);
  const [showVehicleMappingModal, setShowVehicleMappingModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'applied', 'unapplied'

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    applied: 0,
    unapplied: 0,
    misaligned: 0,
    totalCost: 0,
    appliedCost: 0,
    unappliedCost: 0
  });

  useEffect(() => {
    if (isOpen && companyId) {
      loadData();
    }
  }, [isOpen, companyId]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [transactionsData, shipmentsData, costingsData] = await Promise.all([
        fuelTransactionsService.getFuelTransactionsByCompany(companyId),
        shipmentsService.getShipments(companyId),
        shipmentCostingsService.getCostingsByCompany(companyId)
      ]);

      // Enrich transactions with application status
      const enrichedTransactions = transactionsData.map(transaction => {
        const costing = costingsData.find(c => c.shipmentId === transaction.shipment_id);
        const shipment = shipmentsData.find(s => s.id === transaction.shipment_id);

        return {
          ...transaction,
          isApplied: costing && costing.fuel > 0,
          appliedAmount: costing?.fuel || 0,
          shipment: shipment,
          costing: costing,
          // Check if this transaction could be better aligned
          isMisaligned: checkIfMisaligned(transaction, shipmentsData)
        };
      });

      setFuelTransactions(enrichedTransactions);
      setShipments(shipmentsData);
      setCostings(costingsData);
      calculateStats(enrichedTransactions);

    } catch (error) {
      console.error('Error loading bulk fuel data:', error);
      toast.error('Failed to load fuel cost data');
    } finally {
      setLoading(false);
    }
  };

  // Check if a transaction could be better aligned to a different shipment
  const checkIfMisaligned = (transaction, allShipments) => {
    try {
      if (!transaction.shipment_id) return false;

      // Find better matches using the enhanced matching service
      const currentShipment = allShipments.find(s => s.id === transaction.shipment_id);
      if (!currentShipment) return false;

      // Ensure transaction has required fields
      if (!transaction.fill_time) {
        console.warn(`⚠️ Transaction ${transaction.id} missing fill_time for alignment check`);
        return false;
      }

      // Use the enhanced matching algorithm to find the best match
      const betterMatch = enhancedFuelImportService.findMatchingShipmentEnhanced(
        transaction,
        allShipments.filter(s => s.vehiclePlate && s.id !== transaction.shipment_id) // Only shipments with vehicle plates, exclude current
      );

      // If the better match is different from current assignment, it's misaligned
      return betterMatch && betterMatch.id !== transaction.shipment_id;
    } catch (error) {
      console.error(`❌ Error checking alignment for transaction ${transaction.id}:`, error);
      return false;
    }
  };

  const calculateStats = (transactions) => {
    const stats = {
      total: transactions.length,
      applied: 0,
      unapplied: 0,
      misaligned: 0,
      totalCost: 0,
      appliedCost: 0,
      unappliedCost: 0
    };

    transactions.forEach(transaction => {
      stats.totalCost += transaction.cost || 0;

      if (transaction.isApplied) {
        stats.applied++;
        stats.appliedCost += transaction.cost || 0;
      } else {
        stats.unapplied++;
        stats.unappliedCost += transaction.cost || 0;
      }

      if (transaction.isMisaligned) {
        stats.misaligned++;
      }
    });

    setStats(stats);
  };

  // Filter transactions based on search and status
  const filteredTransactions = fuelTransactions.filter(transaction => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        transaction.invoice_number?.toString().toLowerCase().includes(search) ||
        transaction.vehicle_plate?.toLowerCase().includes(search) ||
        transaction.mappedVehiclePlate?.toLowerCase().includes(search) ||
        transaction.shipment?.trackingNumber?.toLowerCase().includes(search) ||
        transaction.station_name?.toLowerCase().includes(search);

      if (!matchesSearch) return false;
    }

    // Status filter
    switch (statusFilter) {
      case 'applied':
        return transaction.isApplied;
      case 'unapplied':
        return !transaction.isApplied;
      case 'misaligned':
        return transaction.isMisaligned;
      default:
        return true;
    }
  });

  // Handle bulk apply fuel costs
  // REMOVED: handleBulkApply - No longer needed with automatic application
  // Transactions are now automatically applied during import
  const handleRefreshData = async () => {
    try {
      setProcessing(true);
      await loadData();
      toast.success(t('fuel.data_refreshed'));
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error(t('fuel.failed_to_refresh_data'));
    } finally {
      setProcessing(false);
    }
  };

  // Handle bulk re-alignment
  const handleBulkReAlign = async () => {
    try {
      setProcessing(true);
      setShowReAlignment(true);

      console.log('🔄 Starting re-alignment analysis...');

      // Get all transactions to re-align (selected ones or all if none selected)
      const transactionsToReAlign = selectedTransactions.size === 0
        ? fuelTransactions.filter(t => t.shipment_id) // Only transactions that are already assigned
        : fuelTransactions.filter(t => selectedTransactions.has(t.id));

      console.log(`📊 Analyzing ${transactionsToReAlign.length} transactions for re-alignment`);

      if (transactionsToReAlign.length === 0) {
        toast(t('fuel.no_transactions_to_realign'), { icon: 'ℹ️' });
        setShowReAlignment(false);
        return;
      }

      // Filter shipments with vehicle plates
      const shipmentsWithVehicles = shipments.filter(s => s.vehiclePlate);
      console.log(`🚛 Available shipments with vehicles: ${shipmentsWithVehicles.length}`);

      if (shipmentsWithVehicles.length === 0) {
        toast.error(t('fuel.no_shipments_with_vehicles'));
        setShowReAlignment(false);
        return;
      }

      // Use the bulk operations service for better error handling
      const reAlignmentResults = [];

      for (const transaction of transactionsToReAlign) {
        try {
          // Ensure transaction has required fields
          if (!transaction.fill_time) {
            console.warn(`⚠️ Transaction ${transaction.id} missing fill_time, skipping`);
            continue;
          }

          // Find better match using enhanced service
          const betterMatch = enhancedFuelImportService.findMatchingShipmentEnhanced(
            transaction,
            shipmentsWithVehicles.filter(s => s.id !== transaction.shipment_id) // Exclude current assignment
          );

          if (betterMatch && betterMatch.id !== transaction.shipment_id) {
            const currentShipment = shipments.find(s => s.id === transaction.shipment_id);

            reAlignmentResults.push({
              transaction,
              currentShipment,
              betterMatch,
              improvement: calculateMatchImprovement(transaction, betterMatch)
            });

            console.log(`✅ Found better match for transaction ${transaction.invoice_number}: ${betterMatch.trackingNumber || betterMatch.id}`);
          }
        } catch (transactionError) {
          console.error(`❌ Error analyzing transaction ${transaction.id}:`, transactionError);
          // Continue with other transactions instead of failing completely
        }
      }

      console.log(`📊 Re-alignment analysis complete: ${reAlignmentResults.length} improvements found`);

      setReAlignmentResults(reAlignmentResults);

      if (reAlignmentResults.length === 0) {
        toast(t('fuel.no_better_alignments_found'), { icon: 'ℹ️' });
        setShowReAlignment(false);
      } else {
        toast.success(t('fuel.realignment_opportunities_found', { count: reAlignmentResults.length }));
      }

    } catch (error) {
      console.error('❌ Error in bulk re-alignment analysis:', error);
      toast.error(t('fuel.failed_to_analyze_realignment') + ': ' + error.message);
      setShowReAlignment(false);
    } finally {
      setProcessing(false);
    }
  };

  // Calculate match improvement score
  const calculateMatchImprovement = (transaction, newShipment) => {
    try {
      // Get transaction time
      let transactionTime;
      if (transaction.fill_time?.toDate) {
        transactionTime = transaction.fill_time.toDate();
      } else if (transaction.fill_time) {
        transactionTime = new Date(transaction.fill_time);
      } else {
        return 'Unknown timing';
      }

      // Get shipment time
      let shipmentTime;
      if (newShipment.deliveryDate) {
        shipmentTime = new Date(newShipment.deliveryDate);
      } else if (newShipment.createdAt?.seconds) {
        shipmentTime = new Date(newShipment.createdAt.seconds * 1000);
      } else if (newShipment.createdAt) {
        shipmentTime = new Date(newShipment.createdAt);
      } else {
        return 'Unknown shipment timing';
      }

      // Validate dates
      if (isNaN(transactionTime.getTime()) || isNaN(shipmentTime.getTime())) {
        return 'Invalid dates';
      }

      const timeDiff = Math.abs(transactionTime - shipmentTime) / (1000 * 60 * 60 * 24); // days

      if (timeDiff === 0) return t('fuel.same_day');
      if (timeDiff <= 1) return t('fuel.next_day');
      if (timeDiff <= 7) return t('fuel.same_week');
      return t('fuel.days_difference', { days: Math.round(timeDiff) });
    } catch (error) {
      console.error('Error calculating match improvement:', error);
      return 'Calculation error';
    }
  };

  // Handle cleanup duplicates
  const handleCleanupDuplicates = async () => {
    try {
      setProcessing(true);

      console.log('🧹 Starting duplicate cleanup...');

      const result = await bulkFuelOperationsService.removeDuplicateFuelApplications(
        companyId,
        user.uid
      );

      console.log('📊 Cleanup result:', result);

      if (result.duplicatesRemoved > 0) {
        toast.success(`Removed ${result.duplicatesRemoved} duplicate applications from ${result.costingsUpdated} costings`);
        await loadData(); // Reload to reflect changes
      } else {
        toast('No duplicates found to clean up', { icon: 'ℹ️' });
      }

    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      toast.error('Failed to cleanup duplicates: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  // Handle delete single transaction
  const handleDeleteTransaction = async (transactionId) => {
    try {
      setDeleting(true);
      
      // First remove from any costing where it's applied
      await fuelTransactionsService.removeFuelTransactionFromCosting(companyId, transactionId);
      
      // Then delete the transaction
      await fuelTransactionsService.deleteFuelTransaction(transactionId);
      
      toast.success(t('fuel.transaction_deleted_successfully'));
      
      // Reload data to reflect changes
      await loadData();
      
    } catch (error) {
      console.error('Error deleting transaction:', error);
      toast.error(t('fuel.failed_to_delete_transaction') + ': ' + error.message);
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(null);
    }
  };

  // Handle delete all transactions
  const handleDeleteAllTransactions = async () => {
    if (!window.confirm(t('fuel.confirm_delete_all_transactions'))) {
      return;
    }

    try {
      setDeleting(true);
      let deletedCount = 0;
      let errorCount = 0;

      // Delete transactions in batches to avoid overwhelming the system
      const batchSize = 10;
      for (let i = 0; i < fuelTransactions.length; i += batchSize) {
        const batch = fuelTransactions.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (transaction) => {
          try {
            // Remove from costing first
            await fuelTransactionsService.removeFuelTransactionFromCosting(companyId, transaction.id);
            // Then delete the transaction
            await fuelTransactionsService.deleteFuelTransaction(transaction.id);
            deletedCount++;
          } catch (error) {
            console.error(`Error deleting transaction ${transaction.id}:`, error);
            errorCount++;
          }
        }));
      }

      if (deletedCount > 0) {
        toast.success(t('fuel.transactions_deleted_successfully', { count: deletedCount }));
      }
      
      if (errorCount > 0) {
        toast.error(t('fuel.some_transactions_failed_to_delete', { count: errorCount }));
      }

      // Reload data to reflect changes
      await loadData();
      
    } catch (error) {
      console.error('Error deleting all transactions:', error);
      toast.error(t('fuel.failed_to_delete_transactions') + ': ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete selected transactions
  const handleDeleteSelected = async () => {
    if (selectedTransactions.size === 0) {
      toast.error(t('fuel.select_transactions_to_delete'));
      return;
    }

    if (!window.confirm(t('fuel.confirm_delete_selected_transactions', { count: selectedTransactions.size }))) {
      return;
    }

    try {
      setDeleting(true);
      let deletedCount = 0;
      let errorCount = 0;

      const transactionIds = Array.from(selectedTransactions);
      
      for (const transactionId of transactionIds) {
        try {
          // Remove from costing first
          await fuelTransactionsService.removeFuelTransactionFromCosting(companyId, transactionId);
          // Then delete the transaction
          await fuelTransactionsService.deleteFuelTransaction(transactionId);
          deletedCount++;
        } catch (error) {
          console.error(`Error deleting transaction ${transactionId}:`, error);
          errorCount++;
        }
      }

      if (deletedCount > 0) {
        toast.success(t('fuel.transactions_deleted_successfully', { count: deletedCount }));
        setSelectedTransactions(new Set()); // Clear selection
      }
      
      if (errorCount > 0) {
        toast.error(t('fuel.some_transactions_failed_to_delete', { count: errorCount }));
      }

      // Reload data to reflect changes
      await loadData();
      
    } catch (error) {
      console.error('Error deleting selected transactions:', error);
      toast.error(t('fuel.failed_to_delete_transactions') + ': ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  // Apply re-alignment results
  const applyReAlignment = async () => {
    if (!reAlignmentResults) return;

    try {
      setProcessing(true);
      let successCount = 0;

      for (const result of reAlignmentResults) {
        try {
          // Update the fuel transaction's shipment assignment
          await fuelTransactionsService.updateFuelTransaction(result.transaction.id, {
            shipment_id: result.betterMatch.id,
            updated_at: new Date(),
            updated_by: user.uid,
            re_aligned: true,
            re_aligned_at: new Date()
          });

          successCount++;
        } catch (error) {
          console.error(`Error re-aligning transaction ${result.transaction.id}:`, error);
        }
      }

      if (successCount > 0) {
        toast.success(`Re-aligned ${successCount} fuel transactions`);
        setShowReAlignment(false);
        setReAlignmentResults(null);
        await loadData();
      }

    } catch (error) {
      console.error('Error applying re-alignment:', error);
      toast.error('Failed to apply re-alignment');
    } finally {
      setProcessing(false);
    }
  };

  // Handle select all/none (only non-applied transactions)
  const handleSelectAll = () => {
    const selectableTransactions = filteredTransactions.filter(t => !t.isApplied);
    const selectableIds = selectableTransactions.map(t => t.id);

    if (selectedTransactions.size === selectableIds.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(selectableIds));
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bulk-fuel-manager bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <FireIcon className="h-6 w-6 text-orange-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              {t('fuel.bulk_fuel_cost_manager')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Statistics Dashboard */}
        <div className="p-6 bg-gray-50 border-b">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
              <div className="text-sm text-blue-800">{t('fuel.total_transactions')}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-600">{stats.applied}</div>
              <div className="text-sm text-green-800">{t('fuel.applied')}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-red-600">{stats.unapplied}</div>
              <div className="text-sm text-red-800">{t('fuel.unapplied')}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.misaligned}</div>
              <div className="text-sm text-yellow-800">{t('fuel.needs_realignment')}</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-600">{formatNumber(stats.totalCost)}</div>
              <div className="text-sm text-purple-800">{t('fuel.total_cost')}</div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-emerald-600">{formatNumber(stats.appliedCost)}</div>
              <div className="text-sm text-emerald-800">{t('fuel.applied_cost')}</div>
            </div>
            <div className="bg-rose-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-rose-600">{formatNumber(stats.unappliedCost)}</div>
              <div className="text-sm text-rose-800">{t('fuel.unapplied_cost')}</div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('fuel.search_transactions')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">{t('fuel.all_transactions')}</option>
                <option value="applied">{t('fuel.applied')}</option>
                <option value="unapplied">{t('fuel.unapplied')}</option>
                <option value="misaligned">{t('fuel.misaligned')}</option>
              </select>
            </div>

            {/* Bulk Actions */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                {selectedTransactions.size} {t('fuel.selected')}
                {(() => {
                  const selectableCount = filteredTransactions.filter(t => !t.isApplied).length;
                  const appliedCount = filteredTransactions.filter(t => t.isApplied).length;
                  return appliedCount > 0 ? ` (${appliedCount} already applied)` : '';
                })()}
              </span>

              <button
                onClick={handleSelectAll}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                {(() => {
                  const selectableTransactions = filteredTransactions.filter(t => !t.isApplied);
                  return selectedTransactions.size === selectableTransactions.length ? t('fuel.deselect_all') : t('fuel.select_all');
                })()}
              </button>

              <button
                onClick={() => setShowVehicleMappingModal(true)}
                className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center space-x-2"
                title={t('fuel.vehicle_mapping_settings')}
              >
                <CogIcon className="h-4 w-4" />
                <span>{t('fuel.vehicle_mapping')}</span>
              </button>

              <button
                onClick={handleRefreshData}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                title={t('fuel.refresh_data_tooltip')}
              >
                {processing ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="h-4 w-4" />}
                <span>{t('fuel.refresh_data')}</span>
              </button>

              <button
                onClick={() => {
                  if (selectedTransactions.size === 0) {
                    handleDeleteAllTransactions();
                  } else {
                    handleDeleteSelected();
                  }
                }}
                disabled={(selectedTransactions.size === 0 && fuelTransactions.length === 0) || deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {deleting ? <LoadingSpinner size="sm" /> : <TrashIcon className="h-4 w-4" />}
                <span>
                  {selectedTransactions.size === 0 
                    ? t('fuel.delete_all') 
                    : t('fuel.delete_selected_count', { count: selectedTransactions.size })
                  }
                </span>
              </button>

              <button
                onClick={handleBulkReAlign}
                disabled={processing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {processing ? <LoadingSpinner size="sm" /> : <ArrowPathIcon className="h-4 w-4" />}
                <span>{t('fuel.realign_transactions')}</span>
              </button>

              <button
                onClick={handleCleanupDuplicates}
                disabled={processing}
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center space-x-2"
              >
                {processing ? <LoadingSpinner size="sm" /> : <ExclamationTriangleIcon className="h-4 w-4" />}
                <span>Cleanup Duplicates</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="table-container">
              <div className="min-w-full">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th style={{ width: '50px' }} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        <input
                          type="checkbox"
                          checked={(() => {
                            const selectableTransactions = filteredTransactions.filter(t => !t.isApplied);
                            return selectedTransactions.size === selectableTransactions.length && selectableTransactions.length > 0;
                          })()}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th style={{ width: '120px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.invoice_number')}
                      </th>
                      <th style={{ width: '140px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.vehicle')}
                      </th>
                      <th style={{ width: '100px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.date')}
                      </th>
                      <th style={{ width: '100px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.cost')}
                      </th>
                      <th style={{ width: '180px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.shipment')}
                      </th>
                      <th style={{ width: '200px' }} className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.status')}
                      </th>
                      <th style={{ width: '80px' }} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        {t('fuel.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map((transaction) => (
                      <tr key={transaction.id} className="hover:bg-gray-50">
                        <td style={{ width: '50px' }} className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedTransactions.has(transaction.id)}
                            disabled={transaction.isApplied} // Disable selection for already applied transactions
                            onChange={(e) => {
                              const newSelected = new Set(selectedTransactions);
                              if (e.target.checked) {
                                newSelected.add(transaction.id);
                              } else {
                                newSelected.delete(transaction.id);
                              }
                              setSelectedTransactions(newSelected);
                            }}
                            className={`rounded border-gray-300 ${transaction.isApplied ? 'opacity-50 cursor-not-allowed' : ''}`}
                          />
                        </td>
                        <td style={{ width: '120px' }} className="px-3 py-3 text-sm font-medium text-gray-900">
                          <div className="truncate">#{transaction.invoice_number || 'N/A'}</div>
                        </td>
                        <td style={{ width: '140px' }} className="px-3 py-3 text-sm text-gray-900">
                          <div className="flex items-center space-x-2">
                            <TruckIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="truncate">{transaction.mappedVehiclePlate || transaction.vehicle_plate}</span>
                          </div>
                        </td>
                        <td style={{ width: '100px' }} className="px-3 py-3 text-sm text-gray-900">
                          <div className="truncate">{formatDate(transaction.fill_time)}</div>
                        </td>
                        <td style={{ width: '100px' }} className="px-3 py-3 text-sm font-medium text-gray-900">
                          <div className="truncate">{formatNumber(transaction.cost)} {t('common.currency')}</div>
                        </td>
                        <td style={{ width: '180px' }} className="px-3 py-3 text-sm text-gray-900">
                          {transaction.shipment ? (
                            <div>
                              <div className="font-medium truncate">#{transaction.shipment.trackingNumber}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {transaction.shipment.origin} → {transaction.shipment.destination}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">{t('fuel.unassigned')}</span>
                          )}
                        </td>
                        <td style={{ width: '200px' }} className="px-3 py-3">
                          <div className="status-column space-y-1">
                            {transaction.isApplied || transaction.status === 'applied' ? (
                              <div>
                                <span className="status-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                  <span>{t('fuel.auto_applied')}</span>
                                </span>
                                {transaction.appliedTransaction && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Applied: {formatNumber(transaction.appliedTransaction.cost)} {t('common.currency')}
                                  </div>
                                )}
                                {transaction.auto_applied && (
                                  <div className="text-xs text-blue-600 mt-1">
                                    ✨ {t('fuel.automatically_applied')}
                                  </div>
                                )}
                              </div>
                            ) : transaction.shipment_id ? (
                              <span className="status-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ClockIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span>{t('fuel.matched_pending_application')}</span>
                              </span>
                            ) : (
                              <span className="status-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span>{t('fuel.unmatched')}</span>
                              </span>
                            )}

                            {transaction.isMisaligned && !transaction.isApplied && (
                              <span className="status-badge inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <ExclamationTriangleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span>{t('fuel.needs_realignment')}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ width: '80px' }} className="px-3 py-3 text-center">
                          <button
                            onClick={() => setShowDeleteConfirmation(transaction.id)}
                            disabled={deleting}
                            className="p-1 text-red-600 hover:text-red-800 disabled:opacity-50"
                            title={t('fuel.delete_transaction')}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredTransactions.length === 0 && (
                  <div className="text-center py-8">
                    <FireIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      {t('fuel.no_fuel_transactions')}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all'
                        ? t('fuel.no_transactions_match_filters')
                        : t('fuel.no_transactions_found')
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Re-alignment Results Modal */}
        {showReAlignment && reAlignmentResults && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  نتائج إعادة الترتيب ({reAlignmentResults.length} تحسين)
                </h3>
                <button
                  onClick={() => setShowReAlignment(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-96">
                <div className="space-y-4">
                  {reAlignmentResults.map((result, index) => (
                    <div key={index} className="border rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">المعاملة</h4>
                          <div className="text-sm text-gray-600">
                            <div>#{result.transaction.invoice_number}</div>
                            <div>{result.transaction.mappedVehiclePlate || result.transaction.vehicle_plate}</div>
                            <div>{formatNumber(result.transaction.cost)} ريال</div>
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">من الشحنة</h4>
                          <div className="text-sm text-gray-600">
                            {result.currentShipment ? (
                              <>
                                <div>#{result.currentShipment.trackingNumber}</div>
                                <div>{result.currentShipment.origin} → {result.currentShipment.destination}</div>
                              </>
                            ) : (
                              <div className="text-red-600">غير مخصص</div>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">إلى الشحنة</h4>
                          <div className="text-sm text-gray-600">
                            <div>#{result.betterMatch.trackingNumber}</div>
                            <div>{result.betterMatch.origin} → {result.betterMatch.destination}</div>
                            <div className="text-green-600 font-medium">
                              تحسين: {result.improvement}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 p-6 border-t">
                <button
                  onClick={() => setShowReAlignment(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={applyReAlignment}
                  disabled={processing}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                >
                  {processing ? <LoadingSpinner size="sm" /> : <CheckIcon className="h-4 w-4" />}
                  <span>تطبيق إعادة الترتيب</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Vehicle Mapping Management Modal */}
        <VehicleMappingManagementModal
          isOpen={showVehicleMappingModal}
          onClose={() => setShowVehicleMappingModal(false)}
          onMappingsUpdated={loadData}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-20">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('fuel.delete_transaction')}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {t('fuel.delete_transaction_confirmation')}
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirmation(null)}
                    disabled={deleting}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    {t('fuel.cancel')}
                  </button>
                  <button
                    onClick={() => handleDeleteTransaction(showDeleteConfirmation)}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center space-x-2"
                  >
                    {deleting ? <LoadingSpinner size="sm" /> : <TrashIcon className="h-4 w-4" />}
                    <span>{t('fuel.delete')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkFuelCostManager;